import { Component, ElementRef, inject, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { JournalApi } from "@ramudden/data-access/resource/journal.api";
import { PlannedAssignmentApi } from "@ramudden/data-access/resource/planned-assignment.api";
import { AssignmentStatus } from "@ramudden/models/assignment";
import { IAttachment } from "@ramudden/models/attachment";
import {
    IPlannedAssignment,
    IPlannedAssignmentCustomTeam,
    IPlannedAssignmentCustomTeamMember,
    IPlannedAssignmentTeamHistory,
} from "@ramudden/models/planned-event";
import { FilterDescriptor, FilterOperator, SearchParameters, ServiceRequestOptions } from "@ramudden/models/search";
import { TaskType } from "@ramudden/models/task";
import { GalleriaService, IGalleriaImage, ModalService, SvgComponent } from "@ramudden/ui";
import { firstValueFrom } from "rxjs";
import { DefaultComponent } from "../../../layout/content/default/default.component";
import { AppService } from "../../../services/app.service";
import { AssignmentService } from "../../../services/assignment.service";
import { DomainDataService } from "../../../services/generic/domain-data.service";
import { LoaderService } from "../../../services/loader.service";
import { TeamDialogComponent, TeamDialogOutput } from "../../setup/dialogs/team/team-dialog.component";
import { ISelectedVehicle, VehicleDialogComponent } from "../../setup/dialogs/vehicle/vehicle-dialog.component";
import { EndSiteDialogComponent } from "./dialogs/end-site/end-site-dialog.component";
import { PdfViewerDialogComponent } from "./dialogs/pdf-viewer/pdf-viewer-dialog.component";

@Component({
    selector: "app-assignment-details",
    standalone: true,
    imports: [
        DefaultComponent,
        ReactiveFormsModule,
        FormsModule,
        TranslateModule,
        SvgComponent,
        VehicleDialogComponent,
        TeamDialogComponent,
        PdfViewerDialogComponent,
        EndSiteDialogComponent,
    ],
    templateUrl: "./assignment-details.component.html",
    styleUrl: "./assignment-details.component.scss",
})
export class AssignmentDetailsComponent implements OnInit {
    @ViewChild("endSiteDialog") endSiteDialog: EndSiteDialogComponent;
    @ViewChild("descriptionEl") descriptionEl: ElementRef<HTMLElement>;

    private readonly appService = inject(AppService);
    private readonly assignmentService = inject(AssignmentService);
    private readonly domainDataService = inject(DomainDataService);
    private readonly formBuilder = inject(FormBuilder);
    private readonly journalApi = inject(JournalApi);
    private readonly loaderService = inject(LoaderService);
    private readonly modalService = inject(ModalService);
    private readonly plannedAssignmentApi = inject(PlannedAssignmentApi);
    private readonly galleriaService = inject(GalleriaService);
    private readonly router = inject(Router);

    plannedAssignment: IPlannedAssignment;
    attachments: IAttachment[] = [];
    plannedEventForm: FormGroup;
    descriptionExpanded = true;
    descriptionOverflows = false;
    generalCollapsed = false;
    teamCollapsed = false;
    administrativeResponsibleCollapsed = false;
    responsibleClientCollapsed = false;
    executiveResponsibleCollapsed = false;
    isRemoveMarkings = false;
    isReadonlyView = false;

    async ngOnInit() {
        this.isReadonlyView = this.appService.isReadonlyView;
        this.plannedAssignment = await this.assignmentService.getSelectedPlannedAssignment();
        if (this.plannedAssignment === undefined) {
            this.appService.clearState();
            return this.router.navigate(["/setup"]);
        }
        this.fetchAttachments();
        this.createForm();

        // Wait for Angular to render the description element before checking overflow
        setTimeout(() => this.checkDescriptionOverflow());
    }

    private checkDescriptionOverflow() {
        const el = this.descriptionEl?.nativeElement;
        if (el) {
            // Measure the text height itself (scrollHeight minus padding) and show
            // the toggle only when it exceeds the number of lines the collapsed
            // view shows. Measured before the toggle/reserved band is applied.
            const style = getComputedStyle(el);
            const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.5;
            const textHeight = el.scrollHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
            const collapsedLines = 3;
            this.descriptionOverflows = textHeight > lineHeight * collapsedLines + 1;
        }
    }

    // region Get
    fileIsPdf(fileName: string): boolean {
        return fileName.toLowerCase().endsWith(".pdf");
    }

    fileIsImage(fileName: string): boolean {
        return (
            fileName.toLowerCase().endsWith(".jpg") ||
            fileName.toLowerCase().endsWith(".jpeg") ||
            fileName.toLowerCase().endsWith(".png") ||
            fileName.toLowerCase().endsWith(".webp")
        );
    }

    get teamMembers(): string {
        if (this.plannedAssignment?.customTeam?.length > 0) {
            return this.plannedAssignment.customTeam
                .map((ct) => `${ct.worker.firstName} ${ct.worker.lastName}`)
                .join(", ");
        }
        return this.appService.selectedWorkers.map((worker) => `${worker.firstName} ${worker.lastName}`).join(", ");
    }

    get vehicles() {
        if (this.plannedAssignment?.customVehicles?.length > 0) {
            return this.plannedAssignment.customVehicles.map((cv) => cv.device.code).join(", ");
        }
        return this.appService.selectedVehicles.map((vehicle) => vehicle.deviceCode).join(", ");
    }

    get siteActive() {
        return (
            this.plannedAssignment.currentTeamStatus?.teamArrival !== undefined &&
            this.plannedAssignment.currentTeamStatus?.teamDeparture === undefined
        );
    }

    get editableAssignment() {
        return this.plannedAssignment.isPlannedToday;
    }

    get siteInactive() {
        return (
            this.plannedAssignment.currentTeamStatus?.teamArrival !== undefined &&
            this.plannedAssignment.currentTeamStatus?.teamDeparture !== undefined
        );
    }
    //endregion

    //region Form
    private createForm() {
        const generalForm = this.createGeneralForm();
        const administrativeResponsibleForm = this.createUserForm("administrativeResponsible");
        const responsibleClientForm = this.createUserForm("responsibleClient");
        const executiveResponsibleForm = this.createUserForm("executiveResponsible");

        const assignment = this.plannedAssignment.assignment;
        const isFinished: boolean =
            assignment.currentStatus &&
            (assignment.currentStatus.statusId === AssignmentStatus.Finished ||
                assignment.currentStatus.statusId === AssignmentStatus.Cancelled);
        this.plannedEventForm = this.formBuilder.group({
            generalForm,
            administrativeResponsibleForm,
            responsibleClientForm,
            executiveResponsibleForm,
            isFinished: new FormControl(isFinished),
        });

        if (!this.editableAssignment) {
            this.plannedEventForm.get("isFinished").disable();
        }
    }

    private createGeneralForm() {
        let description: string;
        if (this.plannedAssignment.assignment?.parentAssignment) {
            description = `${this.plannedAssignment.assignment.parentAssignment.description} - ${this.plannedAssignment.assignment.description}`;
        } else {
            description = this.plannedAssignment.assignment.description;
        }
        const taskType = this.domainDataService.translateEnum("taskType", this.plannedAssignment.assignment.taskTypeId);
        const material = this.domainDataService.translateEnum(
            "signMaterial",
            this.plannedAssignment.assignment.signMaterialId,
        );
        const isMachineWork = this.plannedAssignment.assignment.isMachineWork;
        this.isRemoveMarkings = this.plannedAssignment.assignment.taskTypeId === TaskType.RemoveMarkings;
        const removeType = this.isRemoveMarkings
            ? this.domainDataService.translateEnum("removeType", this.plannedAssignment.assignment.removeTypeId)
            : null;

        const generalForm = this.formBuilder.group({
            description: new FormControl(description),
            isMachineWork: new FormControl(isMachineWork),
            material: new FormControl(material),
            taskType: new FormControl(taskType),
            removeType: new FormControl(removeType),
        });

        generalForm.get("description").disable();
        generalForm.get("description").patchValue(description);
        generalForm.get("isMachineWork").disable();
        generalForm.get("material").disable();
        generalForm.get("taskType").disable();
        generalForm.get("removeType").disable();

        return generalForm;
    }

    private createUserForm(field: string) {
        const assignment = this.plannedAssignment.assignment;

        const user = !assignment.parentAssignment ? assignment[field] : assignment.parentAssignment[field];

        const name = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`;
        const email = user?.email ?? "";
        const phoneNumber = user?.phoneNumber ?? "";
        const mobilePhone = user?.gsm ?? "";

        const userForm = this.formBuilder.group({
            name: new FormControl(name),
            email: new FormControl(email),
            phoneNumber: new FormControl(phoneNumber),
            mobilePhone: new FormControl(mobilePhone),
        });

        userForm.get("name").disable();
        userForm.get("email").disable();
        userForm.get("phoneNumber").disable();
        userForm.get("mobilePhone").disable();

        return userForm;
    }

    //endregion

    //region Site
    async startSite() {
        const plannedAssignmentId = this.plannedAssignment.id;
        this.loaderService.show();
        this.plannedAssignment.currentTeamStatus = await firstValueFrom(
            this.plannedAssignmentApi.marktTeamArrival$(plannedAssignmentId),
        ).finally(() => this.loaderService.hide());
        this.assignmentService.selectedPlannedAssignment = { ...this.plannedAssignment };
    }

    async endSite() {
        const endSite = async () => {
            this.endSiteDialog.openModal({
                teamArrival: this.plannedAssignment.currentTeamStatus.teamArrival,
                teamDeparture: new Date(),
            });
        };

        if (this.plannedEventForm.get("isFinished").value === false) {
            this.modalService.confirm("assignmentPage.endSiteAllowed", endSite, () => {}, "info");
        } else {
            await endSite();
        }
    }

    async onEndSiteDialogSubmit(dates: IPlannedAssignmentTeamHistory) {
        const plannedAssignmentId = this.plannedAssignment.id;
        this.loaderService.show();

        this.plannedAssignment.currentTeamStatus = await firstValueFrom(
            this.plannedAssignmentApi.markTeamDeparture$(plannedAssignmentId, dates),
        ).finally(() => this.loaderService.hide());
        this.assignmentService.selectedPlannedAssignment = { ...this.plannedAssignment };
    }

    //endregion

    //region Dialogs
    async onTeamDialogClosed(teamDialogOutput: TeamDialogOutput) {
        const customTeamMembers = teamDialogOutput.selectedWorkers.map(
            (sw, index) => ({ workerId: sw.id, displayOrder: index }) as IPlannedAssignmentCustomTeamMember,
        );
        const customTeam: IPlannedAssignmentCustomTeam = { teamMembers: customTeamMembers };
        this.plannedAssignment = {
            ...this.plannedAssignment,
            ...(await firstValueFrom(
                this.plannedAssignmentApi.updateCustomTeam(this.plannedAssignment.id, customTeam),
            )),
        };
        this.assignmentService.selectedPlannedAssignment = { ...this.plannedAssignment };
    }

    async onVehicleDialogClosed(selectedVehicles: ISelectedVehicle[]) {
        const customVehicle = {
            vehicles: selectedVehicles.map((sv) => sv.deviceId),
        };

        this.plannedAssignment = {
            ...this.plannedAssignment,
            ...(await firstValueFrom(
                this.plannedAssignmentApi.updateCustomVehicle(this.plannedAssignment.id, customVehicle),
            )),
        };
        this.assignmentService.selectedPlannedAssignment = { ...this.plannedAssignment };
    }
    //endregion

    async toggleFinished(event: Event) {
        const isChecked = (event.target as HTMLInputElement).checked;
        if (isChecked) {
            const answer = await this.assignmentService.setAssignmentFinished();
            if (!answer) {
                this.plannedEventForm.get("isFinished").patchValue(false);
            }
        } else {
            const answer = await this.assignmentService.setAssignmentReopened();
            if (!answer) {
                this.plannedEventForm.get("isFinished").patchValue(true);
            }
        }
    }

    //region API Calls
    private async fetchAttachments() {
        const assignment = this.plannedAssignment.assignment;

        const serviceRequestOptions = new ServiceRequestOptions();
        serviceRequestOptions.includes.add("Journal", "Attachments");

        const searchParameters = new SearchParameters();
        searchParameters.filter = [new FilterDescriptor("IsAdminOnly", false, FilterOperator.equals)];

        this.loaderService.show();
        const journals = (
            await firstValueFrom(
                this.journalApi.getForAssignment$(assignment.id, serviceRequestOptions, searchParameters),
            ).finally(() => this.loaderService.hide())
        ).data;

        if (assignment.project?.id || assignment.parentAssignment?.project?.id) {
            const projectId = assignment.project?.id || assignment.parentAssignment?.project?.id;

            this.loaderService.show();
            const projectJournals = (
                await firstValueFrom(
                    this.journalApi.getForProject$(projectId, serviceRequestOptions, searchParameters),
                ).finally(() => this.loaderService.hide())
            ).data;
            journals.push(...projectJournals);
        }

        this.attachments = journals.map((x) => x.attachments).flat();
    }

    //endregion

    openGalleria(attachment: IAttachment) {
        const images = this.attachments
            .filter((a) => this.fileIsImage(a.url))
            .map((a) => {
                return {
                    previewImageSrc: a.url,
                    source: a.url,
                    title: a.name,
                    alt: a.name,
                } as IGalleriaImage;
            });
        const imageIndex = images.findIndex((i) => i.source === attachment.url);
        this.galleriaService.open(images, imageIndex);
    }
}
